// ============================================================================
// Module 14.0: Workflows - Frontend API Service
// ============================================================================

import {
  WorkflowDefinition,
  WorkflowInstance,
  WorkflowTask,
  WorkflowApproval,
  WorkflowTemplate,
  WorkflowDashboardMetrics,
} from './types';

const API_BASE = '/api/workflows';
const HEADERS = {
  'Content-Type': 'application/json',
  'x-organization-id': 'ORG-XYZ',
};

export const workflowsService = {
  async getDashboardMetrics(): Promise<WorkflowDashboardMetrics> {
    const res = await fetch(`${API_BASE}/analytics/dashboard`, { headers: HEADERS });
    const data = await res.json();
    return data.data;
  },

  async getDefinitions(): Promise<WorkflowDefinition[]> {
    const res = await fetch(`${API_BASE}/definitions`, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async createDefinition(payload: Partial<WorkflowDefinition>): Promise<WorkflowDefinition> {
    const res = await fetch(`${API_BASE}/definitions`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async getInstances(status?: string): Promise<WorkflowInstance[]> {
    const url = status ? `${API_BASE}/instances?status=${status}` : `${API_BASE}/instances`;
    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async startInstance(payload: {
    workflow_definition_id: string;
    business_entity_type: string;
    business_entity_id: string;
    started_by: string;
    context?: Record<string, any>;
  }): Promise<WorkflowInstance> {
    const res = await fetch(`${API_BASE}/instances`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async getTasks(status?: string): Promise<WorkflowTask[]> {
    const url = status ? `${API_BASE}/tasks?status=${status}` : `${API_BASE}/tasks`;
    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async claimTask(taskId: string, userId: string): Promise<WorkflowTask> {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/claim`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ user_id: userId }),
    });
    const data = await res.json();
    return data.data;
  },

  async completeTask(taskId: string, actor: string, comment?: string): Promise<WorkflowTask> {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/complete`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ actor, comment }),
    });
    const data = await res.json();
    return data.data;
  },

  async getApprovals(status?: string): Promise<WorkflowApproval[]> {
    const url = status ? `${API_BASE}/approvals?status=${status}` : `${API_BASE}/approvals`;
    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async submitApprovalDecision(
    approvalId: string,
    approverId: string,
    decision: 'APPROVED' | 'REJECTED' | 'REQUEST_CHANGES',
    comment?: string
  ): Promise<WorkflowApproval> {
    const res = await fetch(`${API_BASE}/approvals/${approvalId}/decision`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ approver_id: approverId, decision, comment }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  },

  async simulateWorkflow(definitionId: string, context: Record<string, any>): Promise<any> {
    const res = await fetch(`${API_BASE}/definitions/${definitionId}/simulate`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ context }),
    });
    const data = await res.json();
    return data.data;
  },

  async getTemplates(): Promise<WorkflowTemplate[]> {
    const res = await fetch(`${API_BASE}/templates`, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },
};
